/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
/* TODO: Add proper typing for API responses to remove these eslint-disable comments */

import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { csrfTokenService } from '../apps/csrf/CsrfTokenService'
import { UserDetails } from '../apps/user-details/types'
import { getLangFallbackUrl } from '../lang/utils/langfetch'

interface UserData {
  userDetails: any // TODO: type
  globalSettings: {
    language?: string
    currency?: string
  }
}

export const useUserDataStore = defineStore('useUserDataStore', () => {
  const _userData = useStorage<UserData>('user-details', {
    userDetails: {},
    globalSettings: {},
  })

  const authStore = useAuthStore(usePinia())

  // Add an event emitter for language changes
  const languageChanged = ref(false)
  const triggerLanguageChanged = () => {
    languageChanged.value = !languageChanged.value
  }

  if (process.client) {
    watch(
      () => authStore.isLoggedIn,
      async (value, oldValue) => {
        if (value && value !== oldValue) {
          _userData.value.userDetails = await _fetchUserData()
          _setGlobalSettings()
          // Signal language may have changed after login
          triggerLanguageChanged()
        }
        if (!value && value !== oldValue) {
          _clearUserData()
        }
      },
    )
  }

  const userDetails = computed(() => {
    return _userData.value.userDetails || {}
  })

  const globalSettings = computed(() => {
    return _userData.value.globalSettings || {}
  })

  const userSettings = computed(() => {
    return _userData.value.userDetails?.settings || {}
  })

  function updateUserData(fieldName: string, value) {
    if (!_userData.value.userDetails) {
      _userData.value.userDetails = {}
    }
    _userData.value.userDetails[fieldName] = value
  }

  function _clearUserData() {
    _userData.value.userDetails = {}
  }

  async function setUserSetting(settingName: string, value: any) {
    if (!_userData.value.userDetails) {
      _userData.value.userDetails = {}
    }
    if (!_userData.value.userDetails.settings) {
      _userData.value.userDetails.settings = {}
    }
    _userData.value.userDetails.settings[settingName] = value
    try {
      await _saveUserSetting(settingName, value)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async function _saveUserSetting(settingName: string, value: any) {
    const { csrfToken } = await csrfTokenService()
    const { locale } = useI18n().i18n
    try {
      const response = await $fetch(
        getLangFallbackUrl(locale, '/_/users/user-settings/'),
        {
          method: 'PATCH',
          body: { [settingName]: value },
          headers: {
            'X-CSRFToken': csrfToken,
          },
        },
      )
      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async function _fetchUserData(): Promise<UserDetails> {
    const { locale } = useI18n().i18n
    const { csrfToken } = await csrfTokenService()
    const url = '/_/users/user-details/'

    return await $fetch(getLangFallbackUrl(locale, url), {
      headers: {
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
    })
  }

  async function saveUserData(firstName: string, lastName: string) {
    try {
      await _fetchSaveUserData(firstName, lastName)
      updateUserData('firstName', firstName)
      updateUserData('lastName', lastName)
    } catch (error) {
      const errorData = error as { data: { errors: any } }
      // eslint-disable-next-line no-throw-literal -- API error format expected by caller
      throw {
        errors: {
          first_name: errorData.data.errors.first_name,
          last_name: errorData.data.errors.last_name,
        },
      }
    }
  }

  async function _fetchSaveUserData(firstName: string, lastName: string) {
    const { locale } = useI18n().i18n
    const { csrfToken } = await csrfTokenService()
    const url = '/_/users/user-details/'

    const formData = new FormData()
    formData.append('first_name', firstName)
    formData.append('last_name', lastName)

    return await $fetch(getLangFallbackUrl(locale, url), {
      method: 'POST',
      body: formData,
      headers: {
        'X-ELDARION-AJAX': 'true',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
    })
  }

  function setGlobalSetting(settingName: string, value: any) {
    if (!_userData.value.globalSettings) {
      _userData.value.globalSettings = {}
    }
    _userData.value.globalSettings[settingName] = value
    // Signal language change when the language setting is updated
    if (settingName === 'language') {
      triggerLanguageChanged()
    }
  }

  function _setGlobalSettings() {
    if (userSettings.value.language) {
      setGlobalSetting('language', userSettings.value.language)
    }
    if (userSettings.value.currency) {
      setGlobalSetting('currency', userSettings.value.currency)
      useCurrencyStore(usePinia()).setCurrentCurrency(
        userSettings.value.currency,
      )
    }
  }

  async function setLanguage(language: string) {
    if (authStore.isLoggedIn) {
      await setUserSetting('language', language)
    }
    setGlobalSetting('language', language)
    // No need to trigger here as setGlobalSetting already does it
  }

  async function setCurrency(currencyCode: string) {
    if (authStore.isLoggedIn) {
      await setUserSetting('currency', currencyCode)
    }
    setGlobalSetting('currency', currencyCode)
  }

  async function deactivateAccount() {
    const { csrfToken } = await csrfTokenService()
    await $fetch('/_/fallback/_/users/user-deactivate/', {
      method: 'POST',
      body: { confirm: true },
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
    })
    authStore.logOut()
  }

  const mapStyleMapping = {
    hiking: 'outdoors-v12',
    satellite: 'satellite-v9',
    road: 'navigation-day-v1',
  }

  const mapStyleForMap = computed(() => {
    return userSettings.value?.map_style &&
      mapStyleMapping[userSettings.value.map_style]
      ? mapStyleMapping[userSettings.value.map_style]
      : 'outdoors-v12'
  })

  function setMapStyle(mapStyle: string) {
    if (authStore.isLoggedIn) {
      void setUserSetting(
        'map_style',
        Object.keys(mapStyleMapping).find(
          (key) => mapStyleMapping[key] === mapStyle,
        ) || 'outdoors-v12',
      )
    }
  }

  return {
    userDetails,
    saveUserData,
    userSettings,
    setUserSetting,
    globalSettings,
    setGlobalSetting,
    setLanguage,
    setCurrency,
    deactivateAccount,
    languageChanged, // Export the signal
    mapStyleForMap,
    setMapStyle,
  }
})
